import React, { useState, useEffect, useContext, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { LangContext } from '../../../context/lang-context'
import { NavigationContext } from '../../../context/navigation-context'
import useFetch from '../../../hooks/fetchMSSQL-hook'
import { NotificationManager } from 'react-notifications'
import { IndexedDbContext } from '../../../db/indexedDbProvider'
import useForm from '../../../hooks/form-hook'
import MachineState from './MachineVI/MachineState'
import ReportButton from '../../../components/Buttons/ReportButton'
import SearchFilter from '../../../components/Filter/SearchFilter'
import ExportWarningModal from '../../../components/Modals/ExportWarningModal'
import NoResults from '../../../components/NoResults/NoResults'

const exportWarningStorageKey = "hideImportExportWarning";

const Machines = () => {
  const { TRL_Pack } = useContext(LangContext);
  const { fetchMssqlApi } = useFetch();
  const importInputRef = useRef(null);
  const { setHeaderData } = useContext(NavigationContext);
  const { IndexedDBAsyncContext } = useContext(IndexedDbContext);
  const { form, openForm, closeForm } = useForm();

  const [machines, setMachines] = useState([]);
  const [favouriteMachines, setFavouriteMachines] = useState([]);
  const [choosedMachines, setChoosedMachines] = useState([]);
  const [machinesCount, setMachinesCount] = useState(0);
  const [summaryMachinesCount, setSummaryMachinesCount] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [summaryInfo, setSummaryInfo] = useState({});
  const [isImporting, setIsImporting] = useState(false);
  const [showExportWarning, setShowExportWarning] = useState(false);

  const reportList = [
    { reportId: 65, reportColor: 'secondary' },
    { reportId: 66, reportColor: 'info' },
    { reportId: 67, reportColor: 'outline-primary' },
    { reportId: 69, reportColor: 'rep-bg-yellow' },
    { reportId: 72, reportColor: 'primary' },
    { reportId: 73, reportColor: 'success' },
    { reportId: 80, reportColor: 'warning' },
    { reportId: 103, reportColor: 'danger' },
    { reportId: 102, reportColor: 'dark' },
    { reportId: 110, reportColor: 'primary' },
    ["osc", "wyw", "dev", "cle"].includes(sessionStorage.getItem("clientId")) && { reportId: 99, reportColor: 'warning' },
  ].filter(Boolean);

  const defaultFilter = {
    page: 1,
    sortBy: "none",
    machineId: null,
    machineName: "",
    serialNo: "",
    locationName: "",
    locationId: null,
    machineTypeName: "",
    machineTypeId: null,
    maintenanceName: "",
    maintenanceTypeId: null,
    isActive: "true",
    machineTags: { id: [], name: [] }
  };

  const [filter, setFilter] = useState(defaultFilter);
  const [settingsFilter, setSettingsFilter] = useState({});

  const rowsPerPage = settingsFilter?.rowsPerPage || filter.rowsPerPage || 50;

  const sortDefaults = {
    MachineName: "ASC",
    LocationName: "ASC",
    MachineTypeName: "ASC",
    Temperature: "DESC",
    HBSuccessRatio: "DESC",
    TotalSales: "DESC",
    TotalSalesCount: "DESC",
    IsActive: "DESC",
  };

  const getSortState = () => {
    const [field, dir] = String(filter.sortBy || "").split("|");
    return { field, dir: (dir || "").toUpperCase() };
  };

  const sortState = getSortState();

  const arrow = (field) => {
    if (sortState.field !== field) return "";
    return sortState.dir === "ASC" ? " ▲" : " ▼";
  };

  const handleHeaderSort = (field) => {
    const [curField, curDirRaw] = String(filter.sortBy || "none").split("|");
    const curDir = (curDirRaw || "").toUpperCase();
    let sortBy;
    let sortByName=""
    if (curField !== field) {sortBy = `${field}|DESC`;
    } else if (curDir === "DESC") {sortBy = `${field}|ASC`;
    } else if (curDir === "ASC") {sortBy = "none";
    } else sortBy = `${field}|DESC`;
    const next = { ...filter, sortBy, page: 1 };
    setFilter(next);
    search(next, settingsFilter.rowsPerPage, 1, true);
  };

  useEffect(() => {
    const handleOnline = () => !isOnline && setIsOnline(true);
    const handleOffline = () => isOnline && setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isOnline]);

  useEffect(() => {
    try {
      const favourite = localStorage.getItem('favouriteMachines');
      if (favourite) {
        setFavouriteMachines(JSON.parse(favourite));
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('favouriteMachines', JSON.stringify(favouriteMachines));
    } catch (err) {
      console.log(err);
    }
  }, [favouriteMachines]);

  const toggleMachineFavourites = (machineId) => {
    setFavouriteMachines((prev) =>
      prev.includes(machineId)
        ? prev.filter((i) => i !== machineId)
        : [...prev, machineId]
    );
  };

  const checkMachineIsFavoutite = (machineId) =>
    favouriteMachines.includes(machineId);

  const getMachinesCount = () =>
    IndexedDBAsyncContext.getItem('otherInfo', 'machinesCount');

  const getRecordedMachinesCount = async () =>
    IndexedDBAsyncContext.getItems('machines')
      .then((m) => m.length)
      .catch((err) => {
        console.log(err);
        return 0;
      });

  useEffect(() => {
    if (sessionStorage.getItem('isMobile')) {
      getMachinesCount()
        .then((count) => {
          setSummaryMachinesCount(count);
        })
        .then(getRecordedMachinesCount)
        .then((res) => {
          setMachinesCount(res);
        });
    }
  }, []);

  useEffect(() => {
    setHeaderData({ text: TRL_Pack.machines.header });
  }, [TRL_Pack, setHeaderData]);

  const getMachineIsChoosed = ({ MachineId }) =>
    choosedMachines.includes(MachineId);

  const deleteMachineFromChoosed = ({ MachineId }) => {
    setChoosedMachines((prev) => prev.filter((i) => i !== MachineId));
  };

  const addMachineToChoosed = ({ MachineId }) => {
    if (getMachineIsChoosed({ MachineId })) {
      deleteMachineFromChoosed({ MachineId });
    } else {
      setChoosedMachines((prev) => [...prev, MachineId]);
    }
  };

  const machineIds = machines.map((m) => m.MachineId);

  const addAllMachinesToChoosed = () => {
    if (choosedMachines.length === machineIds.length) {
      setChoosedMachines([]);
    } else {
      setChoosedMachines(machineIds);
    }
  };

  const openMachineState = (machineId) => {
    if (!navigator.onLine) {
      closeForm();
      return <></>;
    }

    const machine = machines.find((i) => Number(i.MachineId) === Number(machineId));
    if (!machine) {
      NotificationManager.error("can`t find machine");
      closeForm();
      return <></>;
    }

    return (
      <MachineState
        machineId={machine.MachineId}
        header={machine.MachineName}
        closeForm={closeForm}
      />
    );
  };

  const parseThreeState = (val) => {
    if (val === 'true') return true;
    if (val === 'false') return false;
    return null;
  };

  const getMachineIdsByTags = (machineTagIds) => {
    return new Promise((resolve, reject) => {
      fetchMssqlApi(
        "tags/getMachineIdsByTags",
        { method: "POST", hideNotification: true, data: { machineTagIds } },
        (response) => resolve(JSON.stringify(response.map(Number).filter(Number.isFinite))),
        (err) => reject(err)
      );
    });
  };

  const search = async (f = filter, pageSize, page) => {
    const currentFilter = { ...filter, ...f };
    const pageNo = Number(page ?? currentFilter.page ?? 1);
    const rpp = Number(pageSize ?? currentFilter.rowsPerPage ?? rowsPerPage);

    let selectedMachineIds = [];

    if (currentFilter?.machineTags?.id?.length > 0) {
      try {
        const ids = await getMachineIdsByTags(currentFilter.machineTags.id);
        selectedMachineIds = JSON.parse(ids);
      } catch (err) {
        console.log(err);
        selectedMachineIds = [];
      }
    }

    if (currentFilter?.machineId) {
      selectedMachineIds.push(Number(currentFilter.machineId));
    }

    const body = {
      ...currentFilter,
      clientId: sessionStorage.getItem("clientId"),
      page: pageNo,
      rowsPerPage: rpp,
      isActive: parseThreeState(currentFilter.isActive),
      sortBy:
        currentFilter.sortBy && currentFilter.sortBy !== "none"
          ? currentFilter.sortBy
          : null,
      machineIds: JSON.stringify([...new Set(selectedMachineIds)].filter(Number.isFinite)),
    };

    if (navigator.onLine && sessionStorage.getItem("isMobile") !== "true") {
      fetchMssqlApi(
        "machines",
        { method: "POST", data: body, hideNotification: true },
        (response) => {
          const data = Array.isArray(response?.rows)
            ? response.rows
            : Array.isArray(response)
              ? response
              : [];

          setChoosedMachines([]);
          setMachines(data);
          setSummaryInfo(response?.summaryInfo || {});
        }
      );
    } else {
      IndexedDBAsyncContext.getItems("machines")
        .then((ms) => {
          setChoosedMachines([]);
          setMachines(ms);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const badgeStyleSecond = {
    fontWeight: "bold",
    padding: "4px 5px",
    fontSize: "13px",
    borderRadius: "4px",
    color: "white",
  };

  const getHbBadgeClass = (hb) => {
    const value = Number(hb);

    if (hb === null || hb === undefined || hb === "" || !Number.isFinite(value)) {
      return "bg-secondary";
    }

    if (value < 20) return "bg-danger";
    if (value > 80) return "bg-success";
    return "bg-warning";
  };

  const getLastHeartbeatMinutes = (lastHeartbeatDateTime) => {
    if (!lastHeartbeatDateTime) return null;

    const value = String(lastHeartbeatDateTime).trim().toLowerCase();
    const days = Number(value.match(/(\d+)\s*d/)?.[1] || 0);
    const hours = Number(value.match(/(\d+)\s*h/)?.[1] || 0);
    const minutes = Number(value.match(/(\d+)\s*m/)?.[1] || 0);
    const seconds = Number(value.match(/(\d+)\s*s/)?.[1] || 0);

    if (!["d", "h", "m", "s"].some(unit => value.includes(unit))) {
      return null;
    }

    return days * 24 * 60 + hours * 60 + minutes + seconds / 60;
  };

  const getLastHeartbeatBadgeClass = (lastHeartbeatDateTime) => {
    const minutes = getLastHeartbeatMinutes(lastHeartbeatDateTime);

    if (!Number.isFinite(minutes)) return "bg-secondary";
    if (minutes < 15) return "bg-success";
    if (minutes > 24 * 60) return "bg-danger";
    return "bg-warning badge-font-black";
  };

  const machineSortFields = [
    { label: "Nazwa", field: "MachineName", type: "text" },
    { label: "Nr seryjny", field: "SerialNo", type: "text" },
    { label: "Lokalizacja", field: "LocationName", type: "text" },
    { label: "Typ", field: "MachineTypeName", type: "text" },
    { label: "Tryb obsługi", field: "MaintenanceName", type: "text" },
    { label: "Aktywny", field: "IsActive", type: "bool" },
  ];

  const formatSoftware = (str) => {
    if (!str) return null;

    const parts = str.split(",");

    const rows = [];
    for (let i = 0; i < parts.length; i += 2) {
      const first = parts[i]?.trim() || "";
      const second = parts[i + 1]?.trim() || "";
      rows.push(`${first}, ${second},`);
    }

    return rows;
  };

  const cleanJsonString = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v !== "string") return String(v);
    return v.replace(/^"+|"+$/g, "").split(',').join(', ').split(':').join(': ');
  };

  const downloadExcelResponse = (response, fileName) => {
    const data = response?.data || response;
    const blob = data instanceof Blob
      ? data
      : new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleMachinesExport = () => {
    fetchMssqlApi(
      "machines/export",
      { method: "GET", responseType: "blob", hideNotification: true },
      (res) => downloadExcelResponse(res, "Automaty.xlsx"),
      () => NotificationManager.error("Nie udało się pobrać eksportu", "Export")
    );
  };

  const handleExportClick = () => {
    try {
      if (localStorage.getItem(exportWarningStorageKey) === "true") {
        handleMachinesExport();
        return;
      }
    } catch (err) {
      console.log(err);
    }

    setShowExportWarning(true);
  };

  const closeExportWarning = () => {
    setShowExportWarning(false);
  };

  const continueExportWarning = () => {
    setShowExportWarning(false);
    handleMachinesExport();
  };

  const getImportErrorMessage = (error) => {
    const data = error?.response?.data || error?.data || error || {};
    const errors = data?.errors || error?.errors;

    if (Array.isArray(errors) && errors.length) {
      const visibleErrors = errors.slice(0, 6);

      return (
        <div>
          {visibleErrors.map((err, index) => {
            const row = err.RowNumber ? `Wiersz ${err.RowNumber}` : "Plik";
            const field = err.FieldName ? `, ${err.FieldName}` : "";
            const message = err.Message || err.message || "Błąd walidacji";
            const value = err.Value ? ` (${err.Value})` : "";

            return (
              <div key={index}>
                <strong>{row}</strong>
                {field}: {message}{value}
              </div>
            );
          })}

          {errors.length > visibleErrors.length && (
            <div>
              I jeszcze {errors.length - visibleErrors.length} błędów
            </div>
          )}
        </div>
      );
    }

    return data?.message || error?.message || "Nie udało się zaimportować pliku";
  };

  const finishImport = (input) => {
    if (input) {
      input.value = "";
    }

    setIsImporting(false);
  };

  const handleImportChange = (e) => {
    const input = e.target;
    const file = input.files?.[0];

    if (input) {
      input.value = "";
    }

    if (!file || isImporting) return;

    const ext = file.name.split(".").pop()?.toLowerCase();

    if (ext !== "xlsx") {
      NotificationManager.error("Wybierz plik .xlsx", "Import");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setIsImporting(true);

    try {
      fetchMssqlApi(
        "machines/import",
        { method: "POST", data: formData, hideNotification: true },
        res => {
          NotificationManager.success(
            res?.message || "Import zakończony poprawnie",
            "Import"
          );

          search(filter);
          finishImport(input);
        },
        err => {
          NotificationManager.error(
            getImportErrorMessage(err),
            "Błąd importu",
            10000
          );

          finishImport(input);
        }
      );
    } catch (err) {
      NotificationManager.error(
        getImportErrorMessage(err),
        "Błąd importu",
        10000
      );

      finishImport(input);
    }
  };


  const handleSearchFilterChange = useCallback((f) => {
    setFilter(prev =>
      JSON.stringify(prev || {}) === JSON.stringify(f || {}) ? prev : f
    );
  }, []);

  return (
    <div>
      <SearchFilter
        filterName="machines"
        defaultFilter={defaultFilter}
        reportList={reportList}
        searchFilter={filter}
        onFilterChange={handleSearchFilterChange}
        onSettingsChange={(s) => setSettingsFilter((prev) => ({ ...prev, ...s }))}
        search={search}
        totalCount={machines.length}
        dataList={machines}
        forNow
        machineIdsFromCheckbox={choosedMachines}
      />

      <div className="card overflow-auto">
        {sessionStorage.getItem('isMobile') && (
          <div className="card-header pt-0 pb-0 d-flex justify-content-between py-1">
            <div className="font-weight-bold">Wybór automatu</div>
            <div>
              Załadowano: {machinesCount || 0} z{' '} {summaryMachinesCount?.count || 0}
            </div>
          </div>
        )}

        {!sessionStorage.getItem('isMobile') ? (
          <div
            className="card-body overflow-auto p-0 custom-scroll border"
            style={{ maxHeight: window.innerHeight - 150, overflowY: "auto" }}
          >
            <table className="table table-striped">
              <thead>
                <tr className="headers-stick-trxs" style={{ minWidth: "200px" }}>
                  <th className="text-center align-middle p-2">
                    <input
                      type="checkbox"
                      style={{ width: '15px', height: '15px', accentColor: 'var(--primary)' }}
                      checked={choosedMachines.length === machineIds.length}
                      onChange={addAllMachinesToChoosed}
                    />
                  </th>

                  <th className="p-2 align-middle" style={{ minWidth: "200px" }}>
                    <div className="d-flex flex-column align-items-center">
                      <div>Nazwa</div>
                      <div>Nr seryjny</div>
                      <div>Wersja systemu</div>
                    </div>
                  </th>

                  <th className="p-2 align-middle" style={{ minWidth: "200px" }}>
                    <div className="d-flex flex-column align-items-center">
                      <div>Lokalizacja</div>
                    </div>
                  </th>

                  <th className="p-2 align-middle" style={{ minWidth: "200px" }}>
                    <div className="d-flex flex-column align-items-center">
                      <div>Typ</div>
                      <div>Tryb obsługi</div>
                    </div>
                  </th>

                  <th className="p-2 align-middle" style={{ minWidth: "90px" }}>
                    <div className="d-flex flex-column align-items-center">
                      <div>Temperatura</div>
                    </div>
                  </th>

                  <th className="p-2 align-middle" style={{ minWidth: "90px" }}>
                    <div className="d-flex flex-column align-items-center">
                      <div>Stabilność połączenia</div>
                      Ostatnie połączenie
                    </div>
                  </th>

                  <th className="p-2 align-middle" style={{ minWidth: "90px" }}>
                    <div className="d-flex flex-column align-items-center">
                      <div>Sprzedaż całk. wartość (VA101)</div>
                      <div>Sprzedaż całk. ilość (VA102)</div>
                    </div>
                  </th>

                  <th className="p-2 align-middle" style={{ width: "90px" }}>
                    <div className="d-flex flex-column align-items-center">
                      <div>Aktywny</div>
                    </div>
                  </th>

                  <th></th>

                  <th className="p-2 align-middle" style={{ width: "90px" }}>
                    <div className="d-flex align-items-center">
                      <input
                        type="file"
                        ref={importInputRef}
                        className="d-none"
                        accept=".xlsx"
                        disabled={isImporting}
                        onChange={handleImportChange}
                      />

                      <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none text-nowrap mr-2 d-flex align-items-center"
                        onClick={handleExportClick}
                      >
                        <i className="fas fa-file-export mr-1 import-font-smaller" />
                        Export
                      </button>

                      <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none text-nowrap mr-2 d-flex align-items-center"
                        disabled={isImporting}
                        onClick={() => !isImporting && importInputRef.current?.click()}
                      >
                        <i className="fas fa-file-import mr-1 import-font-smaller" />
                        {isImporting ? "Import..." : "Import"}
                      </button>

                      <Link to="/machine/0">
                        <i className="btn btn-outline-secondary fas fa-plus" />
                      </Link>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {machines
                  .slice((filter?.page - 1) * rowsPerPage, filter?.page * rowsPerPage)
                  .map((machine, idx) => (
                    <tr key={idx}>
                      <td>
                        <div className="d-flex w-100 h-100 justify-content-center align-items-center">
                          <input
                            type="checkbox"
                            style={{ width: '15px', height: '15px', accentColor: 'var(--primary)' }}
                            checked={getMachineIsChoosed(machine)}
                            onChange={() => addMachineToChoosed(machine)}
                          />
                        </div>
                      </td>

                      <td className="small">
                        <div>
                          <div>{machine.MachineName}</div>
                          <div>{machine.SerialNo}</div>
                          <div className="align-middle d-flex justify-content-center">
                            {cleanJsonString(machine.SoftwareVersion) ? (
                              <div
                                style={{
                                  maxWidth: 240,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: "13px",
                                }}
                                title={cleanJsonString(machine.SoftwareVersion)}
                              >
                                {formatSoftware(cleanJsonString(machine.SoftwareVersion))?.map((row, i) => (
                                  <div key={i}>{row}</div>
                                ))}
                              </div>
                            ) : (
                              ""
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="small">{machine.LocationName}</td>

                      <td className="small">
                        <div>{machine.MachineTypeName}</div>
                        <div>{machine.MaintenanceName}</div>
                      </td>

                      <td className="align-middle text-center">
                        {(machine.MachineTypeName?.includes('V10') || machine.MachineTypeName?.includes('V11'))
                          ? (machine.Temperature != null ? (`${machine.Temperature}°C`) : ("-"))
                          : ("-")}
                      </td>

                      <td className="p-2 align-middle">
                        <div className="d-flex flex-column align-items-center pe-none" style={{ gap: "8px" }}>
                          {(machine.MachineTypeName?.includes('V10') || machine.MachineTypeName?.includes('V11')) ? (
                            <>
                              <div
                                className={`badge ${getHbBadgeClass(machine.HBSuccessRatio)} d-flex flex-column align-items-center`}
                                style={badgeStyleSecond}
                              >
                                <span>{Number(machine.HBSuccessRatio || 0)}%</span>
                              </div>
                              <div
                                className={`badge ${getLastHeartbeatBadgeClass(machine.LastHeartbeatDateTime)} d-flex flex-column align-items-center`}
                                style={badgeStyleSecond}
                              >
                                <span>{machine.LastHeartbeatDateTime || "-"}</span>
                              </div>
                            </>
                          ) : (
                            "-"
                          )}
                        </div>
                      </td>

                      <td className="align-middle">
                        {machine.TotalSales !== null || machine.TotalSalesCount !== null ? (
                          <>
                            {`${Number(machine.TotalSales ?? 0).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')} zł`}
                            {machine.TotalSalesCount > 0 ? ` (${machine.TotalSalesCount})` : ''}
                          </>
                        ) : (
                          <>-</>
                        )}
                      </td>

                      <td>
                        <div className="d-flex align-items-center justify-content-center">
                          <input
                            type="checkbox"
                            checked={!!machine.IsActive}
                            readOnly
                            style={{
                              width: '15px',
                              height: '15px',
                              accentColor: 'var(--primary)',
                              pointerEvents: 'none'
                            }}
                          />
                        </div>
                      </td>

                      <td>
                        <div className="d-flex justify-content-center">
                          <ReportButton reportId={103} body={() => ({ machineIds: JSON.stringify([machine.MachineId]) })} btnColor="danger" />
                          <ReportButton reportId={73} body={() => ({ machineIds: JSON.stringify([machine.MachineId]) })} btnColor="success" />
                          <ReportButton reportId={72} body={() => ({ machineIds: JSON.stringify([machine.MachineId]) })} btnColor="primary" />
                        </div>
                      </td>

                      <td className="text-center text-nowrap">
                        {machine?.MachineId && (
                          <Link to={`/machine/${machine?.MachineId}`} className="btn btn-sm btn-outline-primary">
                            Edytuj
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {machines?.length === 0 && <NoResults />}
          </div>
        ) : (
          <div className="container">
            {machines &&
              machines.map((i) => (
                <div className="row h-100" key={i.MachineId}>
                  <div
                    className="col h-100 align-self-center"
                    onClick={() => toggleMachineFavourites(i.MachineId)}
                  >
                    {checkMachineIsFavoutite(i.MachineId) ? (
                      <i className="fas fa-star fa-2x" style={{ color: '#0140BE' }} />
                    ) : (
                      <i className="fas fa-star fa-2x" style={{ color: '#C5D8FF' }} />
                    )}
                  </div>

                  <div className="col-6 p-1">
                    <div className="mb-0 font-weight-bold">{i.MachineName}</div>
                    <div className="font-weight-bolder">{i.SerialNo}</div>
                    {i.LastVisit && (
                      <div style={{ fontSize: '1rem' }} className="font-weight-bolder">
                        Stan na: {i.LastVisit}
                      </div>
                    )}
                    {i.LastMoneyInventory && (
                      <div className="font-weight-bolder" style={{ fontSize: '1rem' }}>
                        Monety: {i.LastMoneyInventory}
                      </div>
                    )}
                  </div>

                  <div className="col-4 d-flex flex-column justify-content-center pl-0">
                    {i.MachineId && (
                      <Link
                        style={{ fontSize: '0.75rem' }}
                        to={`/machine/${i.MachineId}`}
                        className="btn btn-sm btn-success mb-1 mt-2 p-0"
                      >
                        Doładowanie
                      </Link>
                    )}
                    <div
                      style={{ fontSize: '0.65rem' }}
                      onClick={() => navigator.onLine && openForm(i.MachineId)()}
                      className={`btn btn-sm btn-primary mb-2 mt-1 p-0 ${!isOnline && 'disabled'}`}
                    >
                      Bieżący stan
                    </div>
                  </div>

                  <hr className="m-0 col-11" />
                </div>
              ))}
          </div>
        )}
      </div>

      <ExportWarningModal
        show={showExportWarning}
        storageKey={exportWarningStorageKey}
        onClose={closeExportWarning}
        onConfirm={continueExportWarning}
      />

      {form && (() => openMachineState(+form))()}
    </div>
  );
};

export default Machines;